import { useForm } from "react-hook-form";
import { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { useNavigate, useParams } from "react-router-dom";
import BackButton from "../common/BackButton";
import BookManagerService from "../../services/dashboard/BookManager";
import CategoryManager from "../../services/dashboard/CategoryManager";
import AuthorManager from "../../services/dashboard/AuthorManager";
import config from "../../config";
import { handleApiResponse } from "../../utils/apiResponseHandler";
import Select from "react-select";
import debounce from "lodash.debounce";
import AsyncSelect from "react-select/async";
import { Controller } from "react-hook-form";

const BookAddForm = ({ setEditingBook, setShowModal }) => {
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
    control,
  } = useForm();

  const customSelectStyles = {
    control: (base, state) => ({
      ...base,
      padding: "0.25rem 0.5rem",
      borderRadius: "0.375rem",
      borderColor: state.isFocused
        ? "#FF864A" // primary-shade4 on focus
        : "#E0E0E0", // primary-shade3 by default
      boxShadow: state.isFocused ? "0 0 0 2px rgba(255, 134, 74, 0.5)" : "none", // focus:ring effect
      "&:hover": {
        borderColor: "#FF864A", // on hover
      },
      backgroundColor: "#fff",
      minHeight: "2.5rem",
      fontSize: "0.875rem",
    }),
    option: (base, state) => ({
      ...base,
      backgroundColor: state.isSelected
        ? "#FF864A"
        : state.isFocused
        ? "#FFE4D9"
        : "#fff",
      color: state.isSelected ? "#fff" : "#000",
      fontSize: "0.875rem",
      cursor: "pointer",
    }),
    menu: (base) => ({
      ...base,
      zIndex: 20,
    }),
  };

  const [bookImages, setBookImages] = useState([]);
  const [authors, setAuthors] = useState([]);
  const [categories, setCategories] = useState([]);
  const navigate = useNavigate();
  const { bookId } = useParams();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [duplicateBooks, setDuplicateBooks] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [bookNameInput, setBookNameInput] = useState("");

  const authorOptions = authors.map((author) => ({
    value: author.authorId,
    label: author.authorName,
  }));

  const categoryOptions = categories.map((category) => ({
    value: category.catId,
    label: category.catName,
  }));

  const loadOptions = async (inputValue, callback) => {
    if (!inputValue || inputValue.length < 3) {
      callback([]);
      return;
    }

    try {
      const result = await BookManagerService.searchBooks(inputValue);
      const books = result.responseData || [];
      const options = books.map((book) => ({
        value: book.bookId,
        label: `${book.bookName} (${book.bookEdition || "N/A"})`,
      }));
      callback(options);
    } catch (err) {
      console.error("Book search failed", err);
      callback([]);
    }
  };

  // Fetch authors and categories
  useEffect(() => {
    const fetchDropdownData = async () => {
      try {
        const [authorRes, categoryRes] = await Promise.all([
          AuthorManager.getAllAuthors(),
          CategoryManager.getAllCategories(),
        ]);

        setAuthors(handleApiResponse(authorRes) || []);
        setCategories(handleApiResponse(categoryRes) || []);
      } catch (error) {
        console.error("Failed to load dropdown data:", error);
        toast.error("Failed to load authors or categories.");
      }
    };

    fetchDropdownData();
  }, []);

  // Load book if editing
  useEffect(() => {
    const fetchBook = async () => {
      if (!bookId || authors.length === 0 || categories.length === 0) return;

      try {
        const res = await BookManagerService.getBookById(bookId);
        const book = handleApiResponse(res);
        if (!book) return;

        const defaults = {
          bookId: book.bookId,
          bookName: book.bookName,
          bookPublication: book.bookPublication,
          bookIsbn10: book.bookIsbn10,
          bookIsbn13: book.bookIsbn13,
          publishedDate: book.publishedDate,
          pages: book.pages,
          height: book.height,
          width: book.width,
          length: book.length,
          weight: book.weight,
          readingAge: book.readingAge,
          bookDescription: book.bookDescription,
          bookCopies: book.bookCopies,
          bookGenres: book.bookGenres,
          bookEdition: book.bookEdition,
          bookLanguage: book.bookLanguage,
          bookStatus: book.bookStatus,
          bookStockStatus: book.bookStockStatus,
          categoryId: book.categoryId,
          authorId: book.authorId,
        };

        reset(defaults);
        setBookImages(book.bookImages || []);
        setBookNameInput(`${book.bookName}`);
      } catch (err) {
        console.error("Failed to load book:", err);
        toast.error("Failed to load book details.");
      }
    };

    fetchBook();
  }, [bookId, authors.length, categories.length, reset]);

  const onSubmit = async (formData) => {
    setIsSubmitting(true);
    try {
      const filteredFormData = { ...formData };
      delete filteredFormData.author;
      delete filteredFormData.category;
      delete filteredFormData.isInWishlist;
      delete filteredFormData.isInCart;

      let res;

      if (bookId) {
        res = await BookManagerService.updateBook(filteredFormData, bookImages);
        if (handleApiResponse(res, "Book updated successfully!")) {
          navigate("/bookManager");
        }
      } else {
        res = await BookManagerService.addBook(filteredFormData, bookImages);
        if (handleApiResponse(res, "Book created successfully!")) {
          navigate("/bookManager");
        }
      }

      reset();
      setBookImages([]);
      if (setEditingBook) setEditingBook(null);
    } catch (error) {
      toast.error(error.message || "Error saving book.");
      console.error(error);
    } finally {
      setIsSubmitting(false); // Stop loader
    }
  };

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      className="bg-primary-shade1 text-text-muted p-8 rounded-xl shadow-xl max-w-4xl mx-auto"
    >
      <div className="mb-6">
        <BackButton />
      </div>

      <h2 className="text-3xl font-semibold text-text-black mb-8 text-center">
        📚 {bookId ? "Edit Book" : "Add New Book"}
      </h2>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Book Name */}
        <div>
          <label className="block mb-2 font-medium">
            Book Name <span className="text-red-600">*</span>
          </label>
          <AsyncSelect
            cacheOptions
            loadOptions={loadOptions}
            defaultOptions={false}
            placeholder="Search or enter book name"
            isClearable
            inputValue={bookNameInput}
            onInputChange={(newValue, { action }) => {
              if (action === "input-change") {
                setBookNameInput(newValue);
                setValue("bookName", newValue, { shouldValidate: true });
              }
            }}
            onChange={(selectedOption) => {
              if (selectedOption) {
                setBookNameInput(selectedOption.label); // Show selected label
                navigate(`/bookForm/${selectedOption.value}`);
              } else {
                setBookNameInput(""); // Clear on remove
                setValue("bookName", "", { shouldValidate: true });
              }
            }}
            components={{
              DropdownIndicator: null,
              IndicatorSeparator: null,
            }}
            styles={{
              control: (provided, state) => ({
                ...provided,
                minHeight: "40px",
                borderRadius: "0.375rem", // rounded
                paddingLeft: "0.75rem",
                paddingRight: "0.75rem",
                borderColor: errors.bookName ? "#dc2626" : "#ff7236", // red-600 or gray-300
                boxShadow: state.isFocused ? "0 0 0 2px #ff7236" : "none", // primary-shade4
                "&:hover": {
                  borderColor: "#ff7236", // gray-400
                },
              }),
              placeholder: (provided) => ({
                ...provided,
                color: "#6b7280", // text-gray-500
                fontSize: "0.875rem",
              }),
              input: (provided) => ({
                ...provided,
                color: "#374151", // text-gray-700 (to match Publication input text)
                fontSize: "1rem",
              }),
              singleValue: (provided) => ({
                ...provided,
                color: "#374151", // text-gray-700
                fontSize: "1rem",
              }),
            }}
          />

          <input
            type="hidden"
            {...register("bookName", {
              required: "Book Name is required",
              minLength: {
                value: 3,
                message: "At least 3 characters required",
              },
            })}
          />
          {errors.bookName && (
            <span className="text-red-600 text-sm">
              {errors.bookName.message}
            </span>
          )}
        </div>

        {/* Publication */}
        <div>
          <label className="block mb-2 font-medium">
            Publication <span className="text-red-600">*</span>
          </label>
          <input
            type="text"
            {...register("bookPublication", {
              required: "Publication is required",
            })}
            placeholder="Enter publication"
            className={`w-full px-4 py-2 rounded border ${
              errors.bookPublication
                ? "border-red-600"
                : "border-primary-shade3"
            } focus:outline-none focus:ring-2 focus:ring-primary-shade4`}
          />
          {errors.bookPublication && (
            <span className="text-red-600 text-sm">
              {errors.bookPublication.message}
            </span>
          )}
        </div>

        {/* ISBN-10 */}
        <div>
          <label className="block mb-2 font-medium">ISBN-10</label>
          <input
            type="text"
            {...register("bookIsbn10", {
              pattern: { value: /^[0-9]{10}$/, message: "Must be 10 digits" },
            })}
            placeholder="Enter ISBN-10"
            className={`w-full px-4 py-2 rounded border ${
              errors.bookIsbn10 ? "border-red-600" : "border-primary-shade3"
            } focus:outline-none focus:ring-2 focus:ring-primary-shade4`}
          />
          {errors.bookIsbn10 && (
            <span className="text-red-600 text-sm">
              {errors.bookIsbn10.message}
            </span>
          )}
        </div>

        {/* ISBN-13 */}
        <div>
          <label className="block mb-2 font-medium">ISBN-13</label>
          <input
            type="text"
            {...register("bookIsbn13", {
              pattern: { value: /^[0-9]{13}$/, message: "Must be 13 digits" },
            })}
            placeholder="Enter ISBN-13"
            className={`w-full px-4 py-2 rounded border ${
              errors.bookIsbn13 ? "border-red-600" : "border-primary-shade3"
            } focus:outline-none focus:ring-2 focus:ring-primary-shade4`}
          />
          {errors.bookIsbn13 && (
            <span className="text-red-600 text-sm">
              {errors.bookIsbn13.message}
            </span>
          )}
        </div>

        {/* Pages */}
        <div>
          <label className="block mb-2 font-medium">
            Pages <span className="text-red-600">*</span>
          </label>
          <input
            type="number"
            {...register("pages", {
              required: "Pages is required",
              min: { value: 1, message: "Minimum 1 page" },
            })}
            placeholder="e.g. 200"
            className={`w-full px-4 py-2 rounded border ${
              errors.pages ? "border-red-600" : "border-primary-shade3"
            } focus:outline-none focus:ring-2 focus:ring-primary-shade4`}
          />
          {errors.pages && (
            <span className="text-red-600 text-sm">{errors.pages.message}</span>
          )}
        </div>

        {/* Published Date */}
        <div>
          <label className="block mb-2 font-medium">Published Date</label>
          <input
            type="date"
            {...register("publishedDate")}
            className={`w-full px-4 py-2 rounded border ${
              errors.publishedDate ? "border-red-600" : "border-primary-shade3"
            } focus:outline-none focus:ring-2 focus:ring-primary-shade4`}
          />
          {errors.publishedDate && (
            <span className="text-red-600 text-sm">
              {errors.publishedDate.message}
            </span>
          )}
        </div>

        {/* Dimensions & Weight */}
        {["height", "width", "length", "weight"].map((field) => (
          <div key={field}>
            <label className="block mb-2 font-medium capitalize">
              {field.charAt(0).toUpperCase() + field.slice(1)}
            </label>
            <input
              type="number"
              step="any"
              {...register(field, {
                min: { value: 0.1, message: `Must be positive` },
              })}
              className={`w-full px-4 py-2 rounded border ${
                errors[field] ? "border-red-600" : "border-primary-shade3"
              } focus:outline-none focus:ring-2 focus:ring-primary-shade4`}
            />
            {errors[field] && (
              <span className="text-red-600 text-sm">
                {errors[field].message}
              </span>
            )}
          </div>
        ))}

        {/* Language */}
        <div>
          <label className="block mb-2 font-medium">
            Language <span className="text-red-600">*</span>
          </label>
          <select
            {...register("bookLanguage", { required: "Language is required" })}
            className={`w-full px-4 py-2 rounded border ${
              errors.bookLanguage ? "border-red-600" : "border-primary-shade3"
            } focus:outline-none focus:ring-2 focus:ring-primary-shade4`}
          >
            <option value="">Select Language</option>
            <option value="English">English</option>
            <option value="Hindi">Hindi</option>
            <option value="Marathi">Marathi</option>
          </select>
          {errors.bookLanguage && (
            <span className="text-red-600 text-sm">
              {errors.bookLanguage.message}
            </span>
          )}
        </div>

        {/* Reading Age */}
        <div>
          <label className="block mb-2 font-medium">
            Reading Age <span className="text-red-600">*</span>
          </label>
          <select
            {...register("readingAge", { required: "Reading age is required" })}
            className={`w-full px-4 py-2 rounded border ${
              errors.readingAge ? "border-red-600" : "border-primary-shade3"
            } focus:outline-none focus:ring-2 focus:ring-primary-shade4 bg-white`}
          >
            <option value="">Select Reading Age</option>
            <option value="CHILD">6-9 years</option>
            <option value="PRE_TEEN">10-12 years</option>
            <option value="TEEN">13-17 years</option>
            <option value="ADULT">18+ years</option>
          </select>
          {errors.readingAge && (
            <span className="text-red-600 text-sm">
              {errors.readingAge.message}
            </span>
          )}
        </div>

        {/* Edition */}
        <div>
          <label className="block mb-2 font-medium">Edition</label>
          <input
            type="text"
            {...register("bookEdition")}
            placeholder="e.g. 1st Edition"
            className={`w-full px-4 py-2 rounded border ${
              errors.bookEdition ? "border-red-600" : "border-primary-shade3"
            } focus:outline-none focus:ring-2 focus:ring-primary-shade4`}
          />
          {errors.bookEdition && (
            <span className="text-red-600 text-sm">
              {errors.bookEdition.message}
            </span>
          )}
        </div>

        {/* Copies */}
        <div>
          <label className="block mb-2 font-medium">
            Copies <span className="text-red-600">*</span>
          </label>
          <input
            type="number"
            {...register("bookCopies", {
              required: "Copies is required",
              min: { value: 0, message: "At least 0" },
            })}
            className={`w-full px-4 py-2 rounded border ${
              errors.bookCopies ? "border-red-600" : "border-primary-shade3"
            } focus:outline-none focus:ring-2 focus:ring-primary-shade4`}
          />
          {errors.bookCopies && (
            <span className="text-red-600 text-sm">
              {errors.bookCopies.message}
            </span>
          )}
        </div>

        {/* Genre */}
        <div>
          <label className="block mb-2 font-medium">
            Genre <span className="text-red-600">*</span>
          </label>
          <select
            {...register("bookGenres", { required: "Genre is required" })}
            className={`w-full px-4 py-2 rounded border ${
              errors.bookGenres ? "border-red-600" : "border-primary-shade3"
            } focus:outline-none focus:ring-2 focus:ring-primary-shade4 bg-white`}
          >
            <option value="">Select Genre</option>
            {/* options... */}
            <option value="FICTION">Fiction</option>
            <option value="NON_FICTION">Non-Fiction</option>
            <option value="MYSTERY">Mystery</option>
            <option value="FANTASY">Fantasy</option>
            <option value="SCIENCE_FICTION">Science Fiction</option>
            <option value="BIOGRAPHY">Biography</option>
            <option value="HISTORY">History</option>
            <option value="ROMANCE">Romance</option>
            <option value="HORROR">Horror</option>
            <option value="POETRY">Poetry</option>
            <option value="THRILLER">Thriller</option>
            <option value="ADVENTURE">Adventure</option>
            <option value="SELF_HELP">Self Help</option>
            <option value="GRAPHIC_NOVEL">Graphic Novel</option>
            <option value="PHILOSOPHY">Philosophy</option>
            <option value="RELIGION">Religion</option>
            <option value="EDUCATIONAL">Educational</option>
            <option value="TRAVEL">Travel</option>
            <option value="COOKING">Cooking</option>
            <option value="ART">Art</option>
            <option value="DRAMA">Drama</option>
          </select>
          {errors.bookGenres && (
            <span className="text-red-600 text-sm">
              {errors.bookGenres.message}
            </span>
          )}
        </div>

        {/* Description */}
        <div className="md:col-span-2">
          <label className="block mb-2 font-medium">
            Description <span className="text-red-600">*</span>
          </label>
          <textarea
            {...register("bookDescription", {
              required: "Description is required",
              minLength: { value: 200, message: "At least 200 characters" },
            })}
            rows={4}
            placeholder="Describe the book..."
            className={`w-full px-4 py-2 rounded border ${
              errors.bookDescription
                ? "border-red-600"
                : "border-primary-shade3"
            } focus:outline-none focus:ring-2 focus:ring-primary-shade4 resize-none`}
          />
          {errors.bookDescription && (
            <span className="text-red-600 text-sm">
              {errors.bookDescription.message}
            </span>
          )}
        </div>

        {/* Category */}
        <div className="mb-4">
          <label className="block mb-2 font-medium">
            Category <span className="text-red-600">*</span>
          </label>
          <Controller
            name="categoryId"
            control={control}
            rules={{ required: "Category is required" }}
            render={({ field }) => (
              <Select
                {...field}
                options={categoryOptions}
                styles={customSelectStyles}
                classNamePrefix="react-select"
                placeholder="Select Category"
                value={
                  categoryOptions.find(
                    (option) => option.value === field.value
                  ) || null
                }
                onChange={(selectedOption) =>
                  field.onChange(selectedOption?.value)
                }
              />
            )}
          />
          {errors.categoryId && (
            <span className="text-red-600 text-sm">
              {errors.categoryId.message}
            </span>
          )}
        </div>

        {/* Author */}
        <div className="mb-4">
          <label className="block mb-2 font-medium">
            Author <span className="text-red-600">*</span>
          </label>
          <Controller
            name="authorId"
            control={control}
            rules={{ required: "Author is required" }}
            render={({ field }) => (
              <Select
                {...field}
                options={authorOptions}
                styles={customSelectStyles}
                classNamePrefix="react-select"
                placeholder="Select Author"
                value={
                  authorOptions.find(
                    (option) => option.value === field.value
                  ) || null
                }
                onChange={(selectedOption) =>
                  field.onChange(selectedOption?.value)
                }
              />
            )}
          />
          {errors.authorId && (
            <span className="text-red-600 text-sm">
              {errors.authorId.message}
            </span>
          )}
        </div>

        {/* Status */}
        <div>
          <label className="block mb-2 font-medium">
            Book Status <span className="text-red-600">*</span>
          </label>
          <select
            {...register("bookStatus", { required: "Status is required" })}
            className={`w-full px-4 py-2 rounded border ${
              errors.bookStatus ? "border-red-600" : "border-primary-shade3"
            } focus:outline-none focus:ring-2 focus:ring-primary-shade4 bg-white`}
          >
            <option value="">Select Status</option>
            <option value="ACTIVE">Active</option>
            <option value="INACTIVE">Inactive</option>
            <option value="ARCHIVED">Archived</option>
            <option value="PENDING_APPROVAL">Pending Approval</option>
          </select>
          {errors.bookStatus && (
            <span className="text-red-600 text-sm">
              {errors.bookStatus.message}
            </span>
          )}
        </div>

        {/* Stock Status */}
        <div>
          <label className="block mb-2 font-medium">
            Book Stock Status
          </label>
          <select
            {...register("bookStockStatus")}
            className={`w-full px-4 py-2 rounded border ${
              errors.bookStockStatus
                ? "border-red-600"
                : "border-primary-shade3"
            } focus:outline-none focus:ring-2 focus:ring-primary-shade4 bg-white`}
          >
            <option value="">Select Stock Status</option>
            <option value="IN_STOCK">In Stock</option>
            <option value="OUT_OF_STOCK">Out of Stock</option>
          </select>
          {errors.bookStockStatus && (
            <span className="text-red-600 text-sm">
              {errors.bookStockStatus.message}
            </span>
          )}
        </div>

        {/* File Upload */}
        <div className="md:col-span-2">
          <label className="block mb-2 font-medium">Upload Book Images</label>
          <div
            className="flex flex-col items-center justify-center w-full p-6 border-2 border-dashed rounded-lg cursor-pointer hover:bg-primary-shade1 transition-colors"
            onClick={() => document.getElementById("fileInput").click()}
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => {
              e.preventDefault();
              const droppedFiles = Array.from(e.dataTransfer.files);
              setBookImages((prev) => [...prev, ...droppedFiles]); // append if needed
            }}
          >
            <svg
              className="w-10 h-10 mb-3 text-primary"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M7 16V4m0 0L3 8m4-4l4 4m6 8v-8m0 0l4 4m-4-4l-4 4"
              />
            </svg>
            <p className="mb-2 text-sm text-gray-500">
              <span className="font-semibold">Click to upload</span> or drag and
              drop
            </p>
            <p className="text-xs text-gray-500">SVG, PNG, JPG (max. 5MB)</p>
            <input
              id="fileInput"
              type="file"
              multiple
              onChange={(e) => {
                // Convert FileList to Array and set it to state
                setBookImages(Array.from(e.target.files));
              }}
              className="hidden"
            />
          </div>

          {/* Preview Section */}
          {bookImages.length > 0 && (
            <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
              {bookImages.map((item, index) => {
                const previewUrl =
                  typeof item === "string"
                    ? item.startsWith("http")
                      ? item
                      : `${config.url}${item}`
                    : URL.createObjectURL(item);
                return (
                  <div
                    key={index}
                    className="relative aspect-[2/3] w-24 sm:w-28 md:w-32 rounded-lg overflow-hidden border border-gray-200 shadow-sm"
                  >
                    <img
                      src={previewUrl}
                      alt={`Preview ${index}`}
                      className="object-contain w-full h-full"
                    />
                    {/* Remove Button */}
                    <button
                      type="button"
                      onClick={() => {
                        setBookImages(bookImages.filter((_, i) => i !== index));
                      }}
                      className="absolute top-1 right-1 bg-red-600 text-white rounded-full p-1 hover:bg-red-700 transition"
                      title="Remove Image"
                    >
                      ✕
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Submit Button */}
      <div className="mt-8 text-center">
        <button
          type="submit"
          disabled={isSubmitting}
          className={`bg-primary text-white px-6 py-3 rounded-lg text-lg transition-all duration-300 ${
            isSubmitting
              ? "opacity-60 cursor-not-allowed"
              : "hover:bg-primary-shade4"
          }`}
        >
          {isSubmitting ? (
            <span className="flex items-center justify-center gap-2">
              <svg
                className="animate-spin h-5 w-5 text-white"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8v8H4z"
                />
              </svg>
              Processing...
            </span>
          ) : (
            <>{bookId ? "Save Book" : "Submit Book"}</>
          )}
        </button>
      </div>
    </form>
  );
};

export default BookAddForm;
